import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { ImageAnnotationResult } from '@euler/app/flows/media/annotation/components/ImageAnnotationView';
import { PredefinedAnnotationTags } from '@euler/app/flows/media/annotation/functions/tagPresets';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useFacadeInspectionContext } from '@euler/app/flows/order/pages/FacadeInspectionScreen/components/FacadeInspectionContext';
import { FacadeScreenEvent } from '@euler/app/flows/order/types';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { mediaStagedToMediaInfo } from '@euler/app/flows/task/functions/task-detail/util';
import { FacadeSitePickerView } from '@euler/app/flows/task/pages/pre-inspection/components/FacadeSitePickerView';
import { FacadeSitesView } from '@euler/app/flows/task/pages/pre-inspection/components/FacadeSitesView';
import { FacadeMediaInfo } from '@euler/app/flows/task/pages/pre-inspection/functions/types';
import {
  facadeSiteRequirementsOf,
  resolveFacadeSites,
} from '@euler/functions/inventory/facadeSiteRequirements';
import {
  facadeDefaultTagOption,
  facadeSiteTagOptions,
} from '@euler/functions/inventory/facadeSiteTagOptions';
import { isRecordOnlySite } from '@euler/functions/inventory/isRecordOnlySite';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useDefaultPreInspectionAnnotationTag } from '@euler/functions/useDefaultPreInspectionAnnotationTag';
import {
  InspectionSiteInfo,
  kFaultFreeSiteCode,
  kFrontRecordsSiteCode,
  kRearRecordsSiteCode,
} from '@euler/model';
import { ImageAnnotationScene, SiteInspectionType } from '@euler/model/enum';
import { array2map } from '@euler/utils/array';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import compareAsc from 'date-fns/compareAsc';
import { memo, useEffect, useMemo } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { BehaviorSubject, Subject } from 'rxjs';

// the pre-inspection is only pushed when the order is created with the
// "完工建单并推送预检报告" option, so the requirement is conditional here.
const kRequiredSitesLabel = '如需建单时推送预检报告，以下项目为必检';

const kContinuousCaptureSiteCodes = new Set<string>([
  kFaultFreeSiteCode,
  kFrontRecordsSiteCode,
  kRearRecordsSiteCode,
]);

export const WiredFacadeSitesView = memo(
  ({
    style,
    event$,
  }: {
    style?: StyleProp<ViewStyle>;
    event$: Subject<FacadeScreenEvent>;
  }) => {
    const {
      formController: { form },
      capabilities,
      facadeInspectionTemplate: template,
      orderDetail$,
      inventoryQuery,
      fileUploader,
    } = useOrderFlowContext();

    const { rpx } = useResponsiveContext();
    const isMobile = useIsMobileLayout();
    const { containerWidth, interacted$ } = useFacadeInspectionContext();
    const readonly = capabilities?.preInspectionReadonly;
    const [orderDetail] = useBehaviorSubject(orderDetail$);
    const profile = useCurrentUserDangerously();
    const getDefaultTag = useDefaultPreInspectionAnnotationTag();

    const siteInspections = useMemo(() => {
      return array2map(
        orderDetail.preInspection?.filter(
          x => x.inspectionType === SiteInspectionType.Facade,
        ) ?? [],
        x => x.siteId,
      );
    }, [orderDetail.preInspection]);

    const requiredSites = useMemo(() => {
      const { requiredSiteIds } = facadeSiteRequirementsOf(template?.conf);
      return resolveFacadeSites(requiredSiteIds, inventoryQuery.getSiteById);
    }, [inventoryQuery, template]);

    const facadeSites = useMemo(() => {
      const list: InspectionSiteInfo[] = [];
      const groups = template?.conf.categories.flatMap(x => x.groups) ?? [];
      const siteIdSet = new Set<number>();
      const disabledSiteIdSet = new Set<number>();
      for (const group of groups) {
        for (const siteId of group.disabledSiteIds ?? []) {
          disabledSiteIdSet.add(siteId);
        }
        for (const siteId of group.siteIds) {
          siteIdSet.add(siteId);
        }
      }
      for (const siteId of siteIdSet) {
        const site = inventoryQuery.getSiteById(siteId);
        if (site != null && !disabledSiteIdSet.has(siteId)) {
          list.push(site);
        }
      }
      return list;
    }, [template, inventoryQuery]);

    const medias = useMemo<FacadeMediaInfo[]>(() => {
      return [...siteInspections.values()]
        .flatMap<FacadeMediaInfo>(siteInspection => {
          return siteInspection.items.flatMap<FacadeMediaInfo>(itemInspection =>
            itemInspection.medias.map(x => ({
              ...mediaStagedToMediaInfo(x),
              siteId: siteInspection.siteId,
              siteCode: siteInspection.siteCode,
              siteName: siteInspection.siteName,
              timestamp: x._timestamp,
            })),
          );
        })
        .sort((x, y) => compareAsc(x.timestamp, y.timestamp));
    }, [siteInspections]);

    const frontRecordCount = medias.filter(
      x => x.siteCode === kFrontRecordsSiteCode,
    ).length;

    const rearRecordCount = medias.filter(
      x => x.siteCode === kRearRecordsSiteCode,
    ).length;

    const takeMedia = useMediaCaptureWithAnnotation({
      scene: ImageAnnotationScene.PreInspection,
    });

    const onSitePress = usePersistFn((code: string) => {
      if (readonly) return;

      const site = inventoryQuery.getSiteByCode(code);
      if (!site) return;

      let target = site;
      let currentSiteId = site.id;

      const title$ = new BehaviorSubject(site.name);

      const onSiteChange = (selected: InspectionSiteInfo) => {
        target = selected;
        currentSiteId = selected.id;
        title$.next(selected.name);
      };

      const uploadVideoFile = async (
        localFileUri: string,
        contentType: string,
        rotate: number | undefined,
      ) => {
        interacted$.next(true);
        fileUploader.enqueue({
          localFileUri,
          contentType,
          type: 'video',
          parameters: {
            cover: true,
            blurhash: true,
            videoRotate: rotate,
          },
          context: {
            type: 'facade-image',
            siteId: currentSiteId,
          },
        });
      };

      const handleImageAnnotationResults = async (
        results: ImageAnnotationResult[],
      ) => {
        interacted$.next(true);
        for (const result of results) {
          fileUploader.enqueue({
            localFileUri: result.imageUri,
            contentType: 'image/jpeg',
            type: 'image',
            // simulateFailure: true,
            parameters: {
              cover: true,
              blurhash: true,
            },
            context: {
              type: 'facade-image',
              siteId: currentSiteId,
              annotationMetadata: result.annotationMetadata,
            },
          });
        }
      };

      takeMedia({
        tags: () => PredefinedAnnotationTags.preInspection,
        tagOptions: () => facadeSiteTagOptions(target),
        defaultTag: () => {
          const isRecord = isRecordOnlySite(target);
          return {
            name: getDefaultTag(isRecord),
            color: !isRecord ? 'warning' : undefined,
            option: isRecord ? undefined : facadeDefaultTagOption(target),
          };
        },
        watermark: (scale?: number) => (
          <TaskImageWatermark
            licensePlateNo={form.licensePlateNo.value}
            title={''}
            title$={title$}
            author={form.serviceAgentInfo.value?.name ?? profile.user.name}
            store={profile.store.name}
            type="pre-inspection"
            scale={scale}
          />
        ),
        caption: () => (
          <FacadeSitePickerView
            defaultSite={site}
            sites={facadeSites ?? []}
            onSelected={onSiteChange}
          />
        ),
        uploadVideoFile,
        handleImageAnnotationResults,
        continuous: isMobile && kContinuousCaptureSiteCodes.has(code),
      });
    });

    const retryUpload = usePersistFn((mediaInfo: FacadeMediaInfo) => {
      if (readonly || mediaInfo.source?.type !== 'local') return;
      fileUploader.enqueue({
        localFileUri: mediaInfo.source.localFileUri,
        contentType: mediaInfo.source.contentType,
        type: mediaInfo.type,
        parameters: mediaInfo.source.uploadParameters,
        context: {
          type: 'facade-image',
          siteId: mediaInfo.siteId,
          mediaKey: mediaInfo.key,
        },
      });
    });

    useEffect(() => {
      if (readonly) return;
      const sub = event$.subscribe(e => {
        if (e.type === 'retryUpload') {
          retryUpload(e.media);
        } else if (e.type === 'add') {
          onSitePress(e.code);
        }
      });
      return () => sub.unsubscribe();
    }, [event$, onSitePress, readonly, retryUpload]);

    return (
      <View
        css={`
          padding: 0 ${rpx(10)}px;
          background-color: #fff;
        `}
        style={style}
      >
        <FacadeSitesView
          noBundle
          readonly={readonly}
          facadeSites={facadeSites}
          siteInspections={siteInspections}
          requiredSites={requiredSites}
          requiredSitesLabel={kRequiredSitesLabel}
          frontRecordCount={frontRecordCount}
          rearRecordCount={rearRecordCount}
          contentWidth={containerWidth - rpx(20)}
          onSitePress={onSitePress}
        />
      </View>
    );
  },
);
